// ─── Role definitions ─────────────────────────────────────────────────────────

export type PlatformRole =
  | "super_admin"
  | "platform_admin"
  | "platform_user" // covers org_admin, engineer, operator
  | null;

/**
 * Role tier used for coarse-grained access checks.
 *
 * platform  → super_admin | platform_admin   (admin dashboard access)
 * org       → organization_admin             (org-scoped access)
 * facility  → engineer | operator            (facility-scoped access)
 */
export type RoleTier = "platform" | "org" | "facility";

export const PLATFORM_ROLES: PlatformRole[] = ["super_admin", "platform_admin"];

export function isPlatformAdmin(role: PlatformRole | null): boolean {
  return role !== null && PLATFORM_ROLES.includes(role);
}

export function isSuperAdmin(role: PlatformRole | null): boolean {
  return role === "super_admin";
}

export function hasFacilityAccess(role: PlatformRole | null): boolean {
  return role === "platform_user";
}

// ─── Page-level access rules ──────────────────────────────────────────────────
// Defines which roles can access each protected page.
// Used by route guards and sidebar filtering — single source of truth.

export type PageKey =
  | "admin_dashboard"
  | "manage_admins"
  | "permissions_manager"
  | "facility_pages"
  | "new_organization"
  | "profile"
  | "facility_summary"
  | "telemetry_outage"
  | "facility_deactivated";

export const PAGE_ACCESS: Record<
  PageKey,
  (role: PlatformRole | null) => boolean
> = {
  admin_dashboard: (role) => isPlatformAdmin(role),
  manage_admins: (role) => isSuperAdmin(role),
  permissions_manager: (role) => isPlatformAdmin(role),
  new_organization: (role) => isPlatformAdmin(role),
  facility_pages: (role) => isPlatformAdmin(role) || hasFacilityAccess(role),
  profile: () => true,
  facility_summary: (role) => isPlatformAdmin(role),
  telemetry_outage: (role) => isPlatformAdmin(role),
  // Landing page for a signed-in user with no active facility to land on —
  // most commonly a platform_user whose only assigned facility has been
  // deactivated. Accessible to anyone authenticated, same as profile.
  facility_deactivated: () => true,
};

export function canAccess(page: PageKey, role: PlatformRole | null): boolean {
  return PAGE_ACCESS[page](role);
}
